import { Link as RouterLink } from "react-router-dom";
import * as z from "zod";
import {
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { signIn, SignInInput } from "@aws-amplify/auth";

import PasswordTextField from "component/Form/PasswordTextField";
import { useFormWithSchema } from "hooks/useFormWithSchema";
import AuthLayout from "component/Layout/AuthLayout";
import { useAppNavigate } from "routes/useAppNavigate";
import { APP_ROUTES } from "routes/appRoutes";
import { loginIdSchema, passwordSchema } from "schemas/userSchema";
import { notify } from "lib/notify";
import PlaceholderLogo from "./PlaceholderLogo";
import { handleError } from "utils/error/handleError";

const schema = z.object({
  loginId: loginIdSchema,
  password: passwordSchema,
});

type LoginFormInput = z.infer<typeof schema>;

const LoginPage = () => {
  const { publicNavigate, authenticatedNavigate } = useAppNavigate();
  const onSubmit = async (data: LoginFormInput) => {
    try {
      const params: SignInInput = {
        username: data.loginId,
        password: data.password,
      };
      const res = await signIn(params);

      if (
        res.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        // 仮パスワードだった場合はパスワード登録画面へ遷移

        publicNavigate.passwordSet({
          state: { signInStep: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" },
        });
        return;
      }

      // ログインが成功した場合はホーム画面へ遷移
      notify("ログインしました");
      authenticatedNavigate.root();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAuthorizedException") {
          notify("ログインID、またはパスワードが間違っています。", "error");
          return;
        }
      }
      handleError(error, {
        overrideMessage: "エラーが発生しています。再度お試しください。",
      });
    }
  };

  const {
    formState: { isSubmitting, errors },
    register,
    onSubmitHandler,
  } = useFormWithSchema(schema, onSubmit);

  return (
    <AuthLayout>
      <Box
        component={"form"}
        onSubmit={onSubmitHandler}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Stack
          spacing={4}
          sx={{
            width: "100%",
          }}
        >
          <Stack spacing={2}>
            <Box display={"flex"} justifyContent={"center"}>
              <PlaceholderLogo />
            </Box>
            <Typography variant="h5">ログイン</Typography>
            <Typography variant="body2" color="text.secondary">
              清掃PFを使用するにはログインが必要です。
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="ログインID"
              {...register("loginId")}
              error={!!errors.loginId}
              helperText={errors.loginId?.message}
              fullWidth
              name="loginId"
              type="text"
              disabled={isSubmitting}
              autoComplete="login-id"
            />
            <PasswordTextField
              label="パスワード"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              fullWidth
              disabled={isSubmitting}
              autoComplete="password"
            />
          </Stack>
          <Stack spacing={2}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={20} color="inherit" />
              }
            >
              ログイン
            </Button>
            <Box textAlign={"right"}>
              <MuiLink
                sx={{
                  display: "inline-block",
                  width: "fit-content",
                }}
                component={RouterLink}
                to={APP_ROUTES.public.passwordResetRequest.path}
              >
                <Typography variant="body2" textAlign={"right"}>
                  パスワードを忘れたら
                </Typography>
              </MuiLink>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default LoginPage;
